import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/Layout";

export default function Login() {
  const { login, lang } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(lang === "fr" ? "Connecté !" : "Signed in!");
      nav("/map");
    } catch {
      toast.error(t(lang, "login_fail"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/map';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "login_title")}</h1>
            <p className="text-slate-600 mt-2 text-sm">{t(lang, "login_sub")}</p>

            <Button data-testid="google-login-btn" onClick={googleLogin} variant="outline" className="w-full mt-6 h-11 rounded-full border-slate-300">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.2 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2v2.9C3.8 20.7 7.6 23 12 23z"/><path fill="#FBBC05" d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7H2c-.7 1.4-1.1 3-1.1 5s.4 3.6 1.1 5l3.7-2.9z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.6L19.3 4C17.5 2.3 15 1.3 12 1.3 7.6 1.3 3.8 3.6 2 7l3.7 2.9C6.6 7.3 9.1 5.4 12 5.4z"/></svg>
              {t(lang, "continue_google")}
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px bg-slate-200 flex-1" /> {t(lang, "or")} <div className="h-px bg-slate-200 flex-1" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t(lang, "email")}</Label>
                <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="password">{t(lang, "password_label")}</Label>
                <Input data-testid="login-password" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
                {loading ? "..." : t(lang, "cta_login")}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-600 mt-6">
              {t(lang, "no_account")} <Link to="/register" data-testid="link-register" className="text-[#1E3A8A] font-semibold hover:underline">{t(lang, "cta_signup")}</Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
